// ============================================
// Scroll.js
// スムーススクロール（Lenis）+ スクロールアニメーション（GSAP ScrollTrigger）
//
// 使用ライブラリ:
//   - Lenis   : https://lenis.darkroom.engineering/
//   - GSAP    : https://gsap.com/
//
// 機能:
//   1. Lenis によるスムーススクロール
//   2. ページ内アンカーリンクへのスムーススクロール
//   3. .js-inview 要素のスクロール検知アニメーション
// ============================================

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

// GSAP に ScrollTrigger プラグインを登録する
// ※ プラグインは必ず使用前に一度だけ登録する
gsap.registerPlugin(ScrollTrigger);

// ============================================
// 設定値
// 挙動を変えたいときはここだけ書き換える
// ============================================
const CONFIG = {
  lenis_lerp: 0.12,
  lenis_duration: 1.6,
  lenis_smoothWheel: true,
  lenis_smoothTouch: false,
  anchor_duration: 1.0,
  anchor_lerp: 0.2,
  inview_start: 'top 75%',
};

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================
// Lenis 初期化
//
// Lenis はデフォルトの window.scroll をラップし、
// なめらかなスクロールを実現するライブラリ。
// GSAP の ticker に乗せることで ScrollTrigger と正確に同期できる。
// ============================================
export const lenis = new Lenis({
  lerp: CONFIG.lenis_lerp,
  duration: prefersReducedMotion ? 0 : CONFIG.lenis_duration,
  smoothWheel: prefersReducedMotion ? false : CONFIG.lenis_smoothWheel,
  smoothTouch: CONFIG.lenis_smoothTouch,
});

lenis.on('scroll', ScrollTrigger.update);
const _raf = (time) => lenis.raf(time * 1000);
gsap.ticker.add(_raf);
gsap.ticker.lagSmoothing(1000, 16);
window.addEventListener('pageshow', () => {
  ScrollTrigger.refresh();
});

// ============================================
// ページ内アンカーリンクへのスムーススクロール
//
// href="#..." の形式のリンクを Lenis 経由でスムーズにスクロールする。
//
// 除外クラス:
//   .-no-smooth          : スムーススクロールを無効にしたいリンク
//   .l-header__skip-content : アクセシビリティ用スキップリンク
//
// ヘッダーの高さは CSS カスタムプロパティ --size-header-h から取得する。
// この値はレスポンシブ CSS 側で定義し、ここでは読み取るだけにする。
// ============================================
function lenisPageScroll() {
  const links = document.querySelectorAll('a[href^="#"]:not(.-no-smooth, .l-header__skip-content)');
  if (links.length === 0) return;

  // CSS カスタムプロパティ --size-header-h からヘッダー高さを取得する
  // fixed ヘッダーが存在する場合、スクロール先がヘッダーの下に来るよう調整する
  const rawHeaderH = getComputedStyle(document.documentElement).getPropertyValue('--size-header-h');
  const headerH = Number.parseFloat(rawHeaderH) || 0;

  // Lenis でスクロールする共通関数
  // offset に負値を指定することでヘッダー分だけ手前で停止する
  // 例: ターゲットが Y=500、ヘッダー=80px → 実際の停止位置 Y=420（要素が80px下に見える）
  const scrollToAnchor = (target) => {
    lenis.scrollTo(target, {
      offset: -headerH,
      lerp: prefersReducedMotion ? 1 : CONFIG.anchor_lerp,
      duration: prefersReducedMotion ? 0 : CONFIG.anchor_duration,
    });
  };

  links.forEach((el) => {
    // data 属性でバインド済みチェック（DOMContentLoaded 後に追加された要素の二重登録防止）
    if (el.dataset.smoothScrollBound === '1') return;
    el.dataset.smoothScrollBound = '1';

    el.addEventListener('click', (e) => {
      let targetSelector = el.getAttribute('href');

      // href="#" のみの場合はページ先頭（body）へスクロール
      if (!targetSelector || targetSelector === '#') {
        e.preventDefault();
        scrollToAnchor('body');
        return;
      }

      // CSS セレクターが有効かつ要素が存在するかを確認してから preventDefault する
      // 存在しない場合はブラウザのデフォルト動作（別ページ遷移など）に任せる
      try {
        if (!document.querySelector(targetSelector)) return;
      } catch {
        // CSS として無効な href（例: "#my:anchor"）は無視する
        return;
      }

      e.preventDefault();
      scrollToAnchor(targetSelector);
    });
  });
}

// ============================================
// スクロール検知アニメーション
//
// .js-inview クラスを持つ要素が画面内に入ると、is-active クラスを追加する。
// CSS 側でフェードインやスライドインのスタイルを定義しておく。
//
// CSS の実装例:
//   .js-inview {
//     opacity: 0;
//     transform: translateY(30px);
//     transition: opacity 0.6s ease, transform 0.6s ease;
//   }
//   .js-inview.is-active {
//     opacity: 1;
//     transform: translateY(0);
//   }
//
// delay 差分でスタッガー（時差アニメーション）を付けたい場合は、
// HTML 側で style="transition-delay: 0.1s" 等を指定する。
// ============================================
function inViewAnm() {
  if (prefersReducedMotion) {
    document.querySelectorAll('.js-inview').forEach((el) => el.classList.add('is-active'));
    return;
  }

  // ScrollTrigger.batch は複数要素をまとめて監視する高効率 API。
  // 個別に ScrollTrigger.create するよりもパフォーマンスが高い。
  ScrollTrigger.batch('.js-inview', {
    start: CONFIG.inview_start,
    once: true, // 一度アクティブになったら再度発火しない（繰り返しアニメーション不要の場合）
    onEnter: (batch) => {
      batch.forEach((el) => el.classList.add('is-active'));
    },
  });
}

// ============================================
// TOPに戻るボタンの表示制御
//
// 範囲外では非表示（opacity: 0 / pointer-events: none）
// 範囲内に入ったときと出るときにふわっとアニメーションする
// ============================================
function topBtnAnm() {
  const btn = document.querySelector('.js-topBtn');
  const hero = document.querySelector('.js-hero');
  const footer = document.querySelector('.js-footer');

  if (!btn || !hero || !footer) return;

  const fadeDuration = prefersReducedMotion ? 0 : 0.48;
  const startY = hero.getBoundingClientRect().bottom + window.scrollY;
  const endY = footer.getBoundingClientRect().top + window.scrollY - 100;

  if (startY >= endY) return;

  ScrollTrigger.create({
    trigger: document.documentElement,
    start: `top+=${startY}px 30%`,
    end: `top+=${endY}px bottom`,
    onEnter: () => gsap.to(btn, { opacity: 1, pointerEvents: 'auto', duration: fadeDuration }),
    onLeave: () => gsap.to(btn, { opacity: 0, pointerEvents: 'none', duration: fadeDuration }),
    onEnterBack: () => gsap.to(btn, { opacity: 1, pointerEvents: 'auto', duration: fadeDuration }),
    onLeaveBack: () => gsap.to(btn, { opacity: 0, pointerEvents: 'none', duration: fadeDuration }),
  });
}

export { lenisPageScroll, inViewAnm, topBtnAnm };
